import { Injectable, inject, signal } from '@angular/core'

import { ApiService } from './api.service'
import { ApiResponse } from '../interfaces/api.interface'

// One bell message (Release B2, CC 11/10/2026): "Maria shared a playlist with you".
export interface InboxItem {
  id: string
  kind: string
  title: string
  body?: string | null
  link?: string | null
  created_at: string
  read: boolean
}

interface InboxResponse {
  unread: number
  items: InboxItem[]
}

// How often the bell asks while the page is open; it also asks when the tab comes back.
const POLL_MS = 60_000

/**
 * The in-app notifications behind the top bar's bell. Polling, not push: a share is not
 * urgent to the second, and a minute-old bell costs one small request a minute.
 */
@Injectable({ providedIn: 'root' })
export class InboxService {
  private api = inject(ApiService)

  readonly items = signal<InboxItem[]>([])
  readonly unread = signal(0)
  private timer: ReturnType<typeof setInterval> | null = null
  private onFocus = () => { if (document.visibilityState === 'visible') void this.refresh() }

  start (): void {
    if (this.timer) return
    void this.refresh()
    this.timer = setInterval(() => void this.refresh(), POLL_MS)
    document.addEventListener('visibilitychange', this.onFocus)
  }

  stop (): void {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    document.removeEventListener('visibilitychange', this.onFocus)
  }

  async refresh (): Promise<void> {
    try {
      const res = await this.api.get<ApiResponse<InboxResponse>>('/notifications')
      this.items.set(res.data?.items ?? [])
      this.unread.set(res.data?.unread ?? 0)
    } catch {
      // A failed poll leaves the bell as it was; the next one tries again.
    }
  }

  // One message read, or all of them (id omitted). The bell updates at once.
  async markRead (id?: string): Promise<void> {
    if (id) {
      const item = this.items().find(i => i.id === id)
      if (!item || item.read) return
      this.items.set(this.items().map(i => (i.id === id ? { ...i, read: true } : i)))
      this.unread.set(Math.max(0, this.unread() - 1))
    } else {
      this.items.set(this.items().map(i => ({ ...i, read: true })))
      this.unread.set(0)
    }
    try {
      await this.api.post('/notifications/read', id ? { id } : {})
    } catch {
      void this.refresh()
    }
  }
}
